// Pagination utility functions

/**
 * Create a slug from text
 * @param {string} text - The text to convert to slug
 * @returns {string} - The slug
 */
export const createSlug = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

/**
 * Build pagination URL with query parameters
 * @param {string} baseUrl - The base API URL
 * @param {Object} params - Pagination parameters
 * @returns {string} - The complete URL with query parameters
 */
export const buildPaginationUrl = (baseUrl, params = {}) => {
  const { page = 1, limit = 20, search = "", country = "" } = params;
  const queryParams = new URLSearchParams();

  if (page > 1) queryParams.append("page", page);
  if (limit !== 20) queryParams.append("limit", limit);
  if (search) queryParams.append("search", search);
  if (country) queryParams.append("country", country);

  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Parse pagination response from API
 * @param {Object} response - API response
 * @returns {Object} - Parsed pagination data
 */
export const parsePaginationResponse = (response) => {
  if (!response || !response.pagination) {
    return {
      data: response?.data || response || [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: response?.data?.length || response?.length || 0,
        itemsPerPage: 20,
        hasNextPage: false,
        hasPrevPage: false,
        nextPage: null,
        prevPage: null,
      },
    };
  }

  return response;
};

/**
 * Generate pagination controls data
 * @param {Object} pagination - Pagination object from API
 * @returns {Object} - Pagination controls data
 */
export const generatePaginationControls = (pagination) => {
  const {
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
  } = pagination;

  // Generate page numbers to show
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    // Show all pages if total is small
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // Show pages around current page
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (end === totalPages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }

  return {
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    pages,
    showFirst: currentPage > 3,
    showLast: currentPage < totalPages - 2,
  };
};
